<template>
  <DashboardLayout>
    <template #header>
      <div class="flex items-center justify-between">
        <div class="flex items-center space-x-4">
          <Link :href="route('settings.roles.index')">
            <Button variant="ghost" size="sm">
              <ArrowLeftIcon class="w-4 h-4 mr-2" />
              Back to Roles
            </Button>
          </Link>
          <div>
            <h1 class="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Permission Matrix
            </h1>
            <p class="mt-1 text-sm text-gray-600 dark:text-gray-400 dark:text-gray-400">
              Manage permissions for all roles in a grid view
            </p>
          </div>
        </div>

        <div class="flex items-center space-x-2">
          <Button
            v-if="hasChanges"
            variant="secondary"
            size="md"
            @click="resetChanges"
          >
            <XMarkIcon class="w-4 h-4 mr-2" />
            Reset
          </Button>
          <Button
            variant="primary"
            size="md"
            :disabled="!hasChanges || saving"
            :loading="saving"
            @click="saveChanges"
          >
            <CheckIcon class="w-4 h-4 mr-2" />
            Save Changes
          </Button>
        </div>
      </div>
    </template>

    <div class="max-w-full mx-auto space-y-6">
      <!-- Statistics Cards -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card class="hover:shadow-lg transition-shadow">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600 dark:text-gray-400">Total Roles</p>
              <h3 class="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                {{ stats.total_roles }}
              </h3>
            </div>
            <div class="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <ShieldCheckIcon class="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </Card>

        <Card class="hover:shadow-lg transition-shadow">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600 dark:text-gray-400">Total Permissions</p>
              <h3 class="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                {{ stats.total_permissions }}
              </h3>
            </div>
            <div class="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
              <KeyIcon class="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </Card>

        <Card class="hover:shadow-lg transition-shadow">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600 dark:text-gray-400">Assignments</p>
              <h3 class="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                {{ stats.total_assignments }}
              </h3>
            </div>
            <div class="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <CheckBadgeIcon class="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </Card>

        <Card class="hover:shadow-lg transition-shadow">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600 dark:text-gray-400">Modules</p>
              <h3 class="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                {{ stats.modules }}
              </h3>
            </div>
            <div class="w-10 h-10 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
              <CubeIcon class="w-6 h-6 text-orange-600 dark:text-orange-400" />
            </div>
          </div>
        </Card>
      </div>

      <!-- Filters and Search -->
      <Card>
        <div class="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <!-- Search -->
          <div class="relative flex-1 max-w-md">
            <MagnifyingGlassIcon class="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              v-model="search"
              type="text"
              placeholder="Search permissions..."
              class="pl-10 w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-100"
            />
          </div>

          <!-- Filters -->
          <div class="flex items-center space-x-4">
            <!-- Module Filter -->
            <select
              v-model="selectedModule"
              class="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-100"
            >
              <option value="">All Modules</option>
              <option
                v-for="module in availableModules"
                :key="module"
                :value="module"
              >
                {{ formatModuleName(module) }}
              </option>
            </select>

            <!-- Show System Roles Toggle -->
            <label class="flex items-center space-x-2 cursor-pointer">
              <input
                v-model="showSystemRoles"
                type="checkbox"
                class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 rounded"
              />
              <span class="text-sm text-gray-700 dark:text-gray-300">
                Show System Roles
              </span>
            </label>

            <!-- Pending Changes Indicator -->
            <Badge v-if="hasChanges" variant="warning" size="md">
              {{ changeCount }} pending changes
            </Badge>
          </div>
        </div>
      </Card>

      <!-- Permission Matrix -->
      <Card>
        <template #header>
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-2">
              <TableCellsIcon class="w-5 h-5 text-gray-500 dark:text-gray-400" />
              <h2 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-gray-100">
                Permission Grid
              </h2>
            </div>
            <div class="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="sm"
                @click="expandAll"
              >
                <ChevronDownIcon class="w-4 h-4 mr-1" />
                Expand All
              </Button>
              <Button
                variant="ghost"
                size="sm"
                @click="collapseAll"
              >
                <ChevronUpIcon class="w-4 h-4 mr-1" />
                Collapse All
              </Button>
            </div>
          </div>
        </template>

        <!-- Legend -->
        <div class="mb-4 flex items-center space-x-6 text-sm">
          <div class="flex items-center space-x-2">
            <div class="w-4 h-4 bg-green-500 rounded flex items-center justify-center">
              <CheckIcon class="w-3 h-3 text-white" />
            </div>
            <span class="text-gray-600 dark:text-gray-400">Directly Granted</span>
          </div>
          <div class="flex items-center space-x-2">
            <div class="w-4 h-4 bg-blue-500 rounded flex items-center justify-center">
              <CheckIcon class="w-3 h-3 text-white" />
            </div>
            <span class="text-gray-600 dark:text-gray-400">Inherited</span>
          </div>
          <div class="flex items-center space-x-2">
            <div class="w-4 h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <span class="text-gray-600 dark:text-gray-400">Not Granted</span>
          </div>
        </div>

        <!-- Scrollable Matrix Container -->
        <div class="overflow-x-auto">
          <div class="inline-block min-w-full align-middle">
            <div class="overflow-hidden border border-gray-200 dark:border-gray-700 rounded-lg">
              <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700 dark:divide-gray-700">
                <!-- Table Header (Roles) -->
                <thead class="bg-gray-50 dark:bg-gray-800 sticky top-0 z-20">
                  <tr>
                    <!-- Empty corner cell -->
                    <th
                      scope="col"
                      class="sticky left-0 z-30 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider border-r border-gray-200 dark:border-gray-700"
                      style="min-width: 300px;"
                    >
                      Permission
                    </th>

                    <!-- Role Headers -->
                    <th
                      v-for="role in filteredRoles"
                      :key="role.id"
                      scope="col"
                      class="px-4 py-3 text-center border-r border-gray-200 dark:border-gray-700 last:border-r-0"
                      style="min-width: 120px;"
                    >
                      <div class="flex flex-col items-center space-y-2">
                        <span class="text-xs font-semibold text-gray-900 dark:text-gray-100">
                          {{ role.name }}
                        </span>
                        <Badge
                          :variant="role.is_system ? 'secondary' : 'primary'"
                          size="xs"
                        >
                          {{ role.is_system ? 'System' : 'Custom' }}
                        </Badge>
                        <button
                          v-if="!role.is_system"
                          type="button"
                          @click="selectAllForRole(role.id)"
                          class="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
                        >
                          Select All
                        </button>
                      </div>
                    </th>
                  </tr>
                </thead>

                <!-- Table Body (Permissions by Module) -->
                <tbody class="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700 dark:divide-gray-700">
                  <template
                    v-for="(moduleData, moduleName) in filteredPermissionsByModule"
                    :key="moduleName"
                  >
                    <!-- Module Header Row -->
                    <tr class="bg-gray-100 dark:bg-gray-800/50">
                      <td
                        colspan="100"
                        class="sticky left-0 z-10 px-6 py-3 border-r border-gray-200 dark:border-gray-700"
                      >
                        <button
                          type="button"
                          @click="toggleModule(moduleName)"
                          class="flex items-center space-x-2 w-full text-left"
                        >
                          <ChevronRightIcon
                            :class="[
                              'w-4 h-4 transition-transform',
                              expandedModules.includes(moduleName) ? 'transform rotate-90' : ''
                            ]"
                          />
                          <component
                            :is="getModuleIcon(moduleName)"
                            class="w-5 h-5 text-primary-600 dark:text-primary-400"
                          />
                          <span class="font-semibold text-sm text-gray-900 dark:text-gray-100">
                            {{ formatModuleName(moduleName) }}
                          </span>
                          <Badge variant="secondary" size="xs">
                            {{ moduleData.permissions.length }} permissions
                          </Badge>
                        </button>
                      </td>
                    </tr>

                    <!-- Permission Rows -->
                    <template v-if="expandedModules.includes(moduleName)">
                      <tr
                        v-for="permission in moduleData.permissions"
                        :key="permission.id"
                        class="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                      >
                        <!-- Permission Name Cell (Sticky) -->
                        <td
                          class="sticky left-0 z-10 bg-white dark:bg-gray-900 px-6 py-4 border-r border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                        >
                          <div class="flex items-center justify-between">
                            <div class="flex-1 min-w-0">
                              <p class="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {{ formatPermissionName(permission.name) }}
                              </p>
                              <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                {{ permission.description || 'No description' }}
                              </p>
                            </div>
                            <button
                              type="button"
                              @click="grantToAllRoles(permission.id)"
                              class="ml-2 text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium whitespace-nowrap"
                            >
                              Grant to All
                            </button>
                          </div>
                        </td>

                        <!-- Permission Checkbox Cells -->
                        <td
                          v-for="role in filteredRoles"
                          :key="`${permission.id}-${role.id}`"
                          class="px-4 py-4 text-center border-r border-gray-200 dark:border-gray-700 last:border-r-0"
                        >
                          <div class="flex items-center justify-center">
                            <button
                              v-if="!role.is_system"
                              type="button"
                              @click="togglePermission(role.id, permission.id)"
                              class="w-6 h-6 rounded border-2 flex items-center justify-center transition-all hover:scale-110"
                              :class="getCheckboxClass(role.id, permission.id)"
                              :title="getCheckboxTitle(role.id, permission.id)"
                            >
                              <CheckIcon
                                v-if="hasPermission(role.id, permission.id)"
                                class="w-4 h-4 text-white"
                              />
                            </button>
                            <div
                              v-else
                              class="w-6 h-6 rounded border-2 flex items-center justify-center"
                              :class="getCheckboxClass(role.id, permission.id)"
                              :title="'System role - read only'"
                            >
                              <CheckIcon
                                v-if="hasPermission(role.id, permission.id)"
                                class="w-4 h-4 text-white"
                              />
                            </div>
                          </div>
                        </td>
                      </tr>
                    </template>
                  </template>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-if="Object.keys(filteredPermissionsByModule).length === 0" class="text-center py-12">
          <MagnifyingGlassIcon class="h-12 w-12 mx-auto text-gray-400 dark:text-gray-600 mb-4" />
          <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            No permissions found
          </h3>
          <p class="text-gray-600 dark:text-gray-400">
            Try adjusting your search or filter criteria
          </p>
        </div>
      </Card>
    </div>

    <!-- Save Confirmation Modal -->
    <Modal :show="showSaveModal" @close="showSaveModal = false">
      <template #title>
        Save Changes
      </template>

      <template #content>
        <div class="text-center">
          <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-primary-100 dark:bg-primary-900/30 mb-4">
            <CheckIcon class="h-6 w-6 text-primary-600 dark:text-primary-400" />
          </div>
          <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            Save {{ changeCount }} permission changes?
          </h3>
          <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
            This will update the permissions for the affected roles.
          </p>
        </div>
      </template>

      <template #footer>
        <div class="flex justify-end space-x-3">
          <Button variant="secondary" @click="showSaveModal = false">
            Cancel
          </Button>
          <Button variant="primary" @click="confirmSave">
            <CheckIcon class="w-4 h-4 mr-2" />
            Save Changes
          </Button>
        </div>
      </template>
    </Modal>
  </DashboardLayout>
</template>

<script setup>
import { ref, computed } from 'vue';
import { Link, router } from '@inertiajs/vue3';
import DashboardLayout from '@/Layouts/DashboardLayout.vue';
import Card from '@/Components/Common/Card.vue';
import Button from '@/Components/Common/Button.vue';
import Badge from '@/Components/Common/Badge.vue';
import Modal from '@/Components/Common/Modal.vue';
import {
  ArrowLeftIcon,
  XMarkIcon,
  CheckIcon,
  ShieldCheckIcon,
  KeyIcon,
  CheckBadgeIcon,
  CubeIcon,
  MagnifyingGlassIcon,
  TableCellsIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronRightIcon,
  Cog6ToothIcon,
  WrenchIcon,
  BellIcon,
  ChartBarIcon,
  UserGroupIcon,
} from '@heroicons/vue/24/outline';
import { useToast } from '@/Composables/useToast';

const props = defineProps({
  roles: {
    type: Array,
    required: true,
  },
  permissions: {
    type: Array,
    required: true,
  },
  permissions_by_module: {
    type: Object,
    required: true,
  },
  matrix: {
    type: Object,
    required: true,
  },
  stats: {
    type: Object,
    required: true,
  },
});

const { success, error: showError } = useToast();

// State
const search = ref('');
const selectedModule = ref('');
const showSystemRoles = ref(true);
const expandedModules = ref([]);
const localMatrix = ref(JSON.parse(JSON.stringify(props.matrix)));
const showSaveModal = ref(false);
const saving = ref(false);

// Available modules
const availableModules = computed(() => {
  return Object.keys(props.permissions_by_module);
});

// Filtered roles
const filteredRoles = computed(() => {
  return props.roles.filter(role => {
    if (!showSystemRoles.value && role.is_system) {
      return false;
    }
    return true;
  });
});

// Filtered permissions by module
const filteredPermissionsByModule = computed(() => {
  let modules = props.permissions_by_module;

  // Filter by selected module
  if (selectedModule.value) {
    modules = {
      [selectedModule.value]: modules[selectedModule.value]
    };
  }

  // Filter by search
  if (search.value) {
    const searchLower = search.value.toLowerCase();
    const filtered = {};

    Object.entries(modules).forEach(([moduleName, moduleData]) => {
      const filteredPermissions = moduleData.permissions.filter(permission =>
        permission.name.toLowerCase().includes(searchLower) ||
        permission.description?.toLowerCase().includes(searchLower)
      );

      if (filteredPermissions.length > 0) {
        filtered[moduleName] = {
          ...moduleData,
          permissions: filteredPermissions,
        };
      }
    });

    return filtered;
  }

  return modules;
});

// Has changes
const hasChanges = computed(() => {
  return JSON.stringify(localMatrix.value) !== JSON.stringify(props.matrix);
});

// Change count
const changeCount = computed(() => {
  let count = 0;

  Object.keys(localMatrix.value).forEach(roleId => {
    Object.keys(localMatrix.value[roleId]).forEach(permissionId => {
      const original = props.matrix[roleId]?.[permissionId] || false;
      const current = localMatrix.value[roleId][permissionId];

      if (original !== current) {
        count++;
      }
    });
  });

  return count;
});

/**
 * Format module name
 */
const formatModuleName = (name) => {
  return name
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Format permission name
 */
const formatPermissionName = (name) => {
  const parts = name.split('.');
  if (parts.length >= 2) {
    return parts.slice(1).map(p =>
      p.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
    ).join(' ');
  }
  return name;
};

/**
 * Get module icon
 */
const getModuleIcon = (moduleName) => {
  const icons = {
    'system': Cog6ToothIcon,
    'it_helpdesk': WrenchIcon,
    'maintenance': WrenchIcon,
    'incidents': BellIcon,
    'dashboard': ChartBarIcon,
    'reports': ChartBarIcon,
    'team': UserGroupIcon,
  };

  return icons[moduleName] || CubeIcon;
};

/**
 * Check if role has permission
 */
const hasPermission = (roleId, permissionId) => {
  return localMatrix.value[roleId]?.[permissionId] || false;
};

/**
 * Get checkbox class based on state
 */
const getCheckboxClass = (roleId, permissionId) => {
  const hasIt = hasPermission(roleId, permissionId);
  const isChanged = props.matrix[roleId]?.[permissionId] !== localMatrix.value[roleId]?.[permissionId];

  if (hasIt) {
    if (isChanged) {
      return 'bg-yellow-500 border-yellow-600 hover:bg-yellow-600';
    }
    return 'bg-green-500 border-green-600 hover:bg-green-600';
  }

  return 'bg-gray-200 dark:bg-gray-700 border-gray-300 dark:border-gray-600 hover:bg-gray-300 dark:hover:bg-gray-600';
};

/**
 * Get checkbox title
 */
const getCheckboxTitle = (roleId, permissionId) => {
  const hasIt = hasPermission(roleId, permissionId);
  const isChanged = props.matrix[roleId]?.[permissionId] !== localMatrix.value[roleId]?.[permissionId];

  if (hasIt) {
    if (isChanged) {
      return 'Permission will be granted (pending save)';
    }
    return 'Permission granted - click to revoke';
  }

  return 'Permission not granted - click to grant';
};

/**
 * Toggle permission for role
 */
const togglePermission = (roleId, permissionId) => {
  if (!localMatrix.value[roleId]) {
    localMatrix.value[roleId] = {};
  }

  localMatrix.value[roleId][permissionId] = !localMatrix.value[roleId][permissionId];
};

/**
 * Select all permissions for a role
 */
const selectAllForRole = (roleId) => {
  if (!localMatrix.value[roleId]) {
    localMatrix.value[roleId] = {};
  }

  props.permissions.forEach(permission => {
    localMatrix.value[roleId][permission.id] = true;
  });
};

/**
 * Grant permission to all roles
 */
const grantToAllRoles = (permissionId) => {
  filteredRoles.value.forEach(role => {
    if (!role.is_system) {
      if (!localMatrix.value[role.id]) {
        localMatrix.value[role.id] = {};
      }
      localMatrix.value[role.id][permissionId] = true;
    }
  });
};

/**
 * Toggle module expansion
 */
const toggleModule = (moduleName) => {
  const index = expandedModules.value.indexOf(moduleName);
  if (index > -1) {
    expandedModules.value.splice(index, 1);
  } else {
    expandedModules.value.push(moduleName);
  }
};

/**
 * Expand all modules
 */
const expandAll = () => {
  expandedModules.value = Object.keys(filteredPermissionsByModule.value);
};

/**
 * Collapse all modules
 */
const collapseAll = () => {
  expandedModules.value = [];
};

/**
 * Reset changes
 */
const resetChanges = () => {
  localMatrix.value = JSON.parse(JSON.stringify(props.matrix));
};

/**
 * Save changes
 */
const saveChanges = () => {
  showSaveModal.value = true;
};

/**
 * Confirm save
 */
const confirmSave = () => {
  saving.value = true;

  // Collect changes
  const changes = [];

  Object.keys(localMatrix.value).forEach(roleId => {
    Object.keys(localMatrix.value[roleId]).forEach(permissionId => {
      const original = props.matrix[roleId]?.[permissionId] || false;
      const current = localMatrix.value[roleId][permissionId];

      if (original !== current) {
        changes.push({
          role_id: parseInt(roleId),
          permission_id: parseInt(permissionId),
          granted: current,
        });
      }
    });
  });

  router.post(route('settings.permissions.matrix.update'), {
    changes: changes,
  }, {
    onSuccess: () => {
      success(`Successfully updated ${changes.length} permission(s)`);
      showSaveModal.value = false;
      saving.value = false;
    },
    onError: (errors) => {
      showError(errors.message || 'Failed to save changes. Please try again.');
      saving.value = false;
    },
  });
};

// Auto-expand first module
if (Object.keys(filteredPermissionsByModule.value).length > 0) {
  expandedModules.value = [Object.keys(filteredPermissionsByModule.value)[0]];
}
</script>
